import React, { useMemo } from 'react';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { useParamBackUrl } from '../../../common/hooks/use-param-back-url';
import { selectSite } from '../../site/siteSlice';
import { DeviceIcon } from './components/DeviceIcon';
import { HARDWARE_TYPE_TO_UI_CONFIG } from './constants';
import { DetailsConfigurationButton } from './details/DetailsConfigurationButton';
import { DetailsInfo } from './details/DetailsInfo';
import { VisibleHardwareType } from './types';

export function HardwareDetails() {
  const { t } = useTranslation();
  const { devices } = useSelector(selectSite);
  const { deviceId } = useParams<{ deviceId: string }>();

  const device = useMemo(() => devices.find((device) => device.id === Number(deviceId)), [devices, deviceId]);

  const isLoading = false;

  const { icon, title, subtitle } = useMemo(() => {
    const iconAndTitle = HARDWARE_TYPE_TO_UI_CONFIG[(device?.device_type as VisibleHardwareType) ?? 'EV_CHARGER'];

    return {
      ...iconAndTitle,
      subtitle: undefined,
    };
  }, [device?.device_type]);

  const displayConfiguration = true;

  const backURL = useParamBackUrl(`../../list?direction=back`);

  return (
    <SlidingAnimationPageBase title={t('Hardware.hardware')} backURL={backURL} pb={6}>
      <CenteredLoader hidden={!isLoading} text={`${t('Common.loading')}...`} />
      {device && !isLoading && (
        <Box px={5}>
          <DeviceIcon Icon={icon} title={title} subtitle={subtitle} />

          <DetailsInfo device={device} />

          {displayConfiguration && <DetailsConfigurationButton device={device} />}
        </Box>
      )}
      {!device && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>{t('Hardware.error fetching device')}</AlertTitle>
          <AlertDescription>
            {t('Common.please try again')} {t('Common.if this persists contact support')}
          </AlertDescription>
        </Alert>
      )}
    </SlidingAnimationPageBase>
  );
}
